// @flow

import React from "react";
import { localize } from "redux-i18n";

import { formatDate } from "@transformers/date";
import { Button, GSGrid, View, Text, SwitchButton, BoxField } from "@components";
import type {
  ManagerSmsSettings,
  ManagerSmsRestriction,
} from "@modules/service/reducer/manager-sms-notifications/types";
import type { EmployeeSmsSettings } from "@modules/service/reducer/employee-sms-notifications/types";

type SwitchDataItem = {
  value: boolean,
  title: string,
  description: string,
  onChange: (value: boolean) => void,
  hidden?: boolean,
};

type Props = $Exact<{
  list: Array<ManagerSmsRestriction>,
  settings: ?ManagerSmsSettings,
  employeeSettings: ?EmployeeSmsSettings,
  onUpdateSettings: Function,
  onUpdateSmsSettings: Function,
  onAdd: Function,
  onEdit: Function,
  onDelete: Function,
  ...PropsFromLocalize,
}>;

export function ManagerSmsNotificationsSmsList(props: Props) {
  const columns = [
    {
      name: props.t("MANAGER_SMS_NOTIFICATIONS_RULE_ID"),
      key: "id",
    },
    {
      name: props.t("MANAGER_SMS_NOTIFICATIONS_START_TIME"),
      key: "startTime",
    },
    {
      name: props.t("MANAGER_SMS_NOTIFICATIONS_END_TIME"),
      key: "endTime",
    },
    {
      name: props.t("MANAGER_SMS_HIERARCHY_AREAS"),
      key: "hierarchyAreas",
      renderCell: ({ row }: { row: ManagerSmsRestriction }) => (
        <div className="d-flex charcoal-dark align-items-center">
          <span className="material-icons pointer mr-2">folder</span>
          <div className="charcoal-dark">{(row.hierarchyAssignments || []).length}</div>
        </div>
      ),
    },
    {
      name: props.t("MANAGER_SMS_DATE_ADDED"),
      key: "dateAdded",
      renderCell: ({ row }: { row: ManagerSmsRestriction }) => formatDate(row.createdAt),
    },
    {
      name: props.t("EDIT"),
      key: "edit",
      width: 50,
      align: "center",
      onCellClick: row => props.onEdit(row),
      renderCell: ({ row }: { row: ManagerSmsRestriction }) =>
        isSmsNotificationsActive ? GSGrid.render.materialIcon("edit") : <></>,
    },
    {
      name: props.t("DELETE"),
      key: "delete",
      width: 50,
      align: "center",
      onCellClick: row => props.onDelete(row),
      renderCell: ({ row }: { row: ManagerSmsRestriction }) =>
        isSmsNotificationsActive ? GSGrid.render.materialIcon("delete") : <></>,
    },
  ];
  const isSmsNotificationsActive = (props.settings || {}).smsNotificationsEnabled;
  const isEmployeeRefActive = (props.settings || {}).includeEmployeeRef;
  const isAbsenceRemindersActive = (props.employeeSettings || {}).overdueAbsenceReminders;
  const isTextToCloseActive = (props.employeeSettings || {}).textToClose;
  const includeEmployeeRefOptions = [
    {
      label: props.t("MANAGER_SMS_NOTIFICATIONS_INCLUDE"),
      value: true,
      active: !!isEmployeeRefActive,
      activeColor: "pumpkin-orange",
    },
    {
      label: props.t("EXCLUDE"),
      value: false,
      active: !isEmployeeRefActive,
      activeColor: "pumpkin-orange",
    },
  ];

  function updateEmployeeSmsSetting(value: string) {
    return function(enabled: boolean) {
      props.onUpdateSmsSettings({ ...props.employeeSettings, [value]: enabled });
    };
  }

  function updateManagerSmsSettings(value: string) {
    return function(enabled: boolean) {
      props.onUpdateSettings({ ...props.settings, [value]: enabled });
    };
  }

  const switchData: SwitchDataItem[] = [
    {
      value: isSmsNotificationsActive,
      title: props.t("MANAGER_SMS_NOTIFICATIONS_TEXT"),
      description: props.t("ABSENCE_NOTIFICATION_SWITCH_DESCRIPTION"),
      onChange: updateManagerSmsSettings("smsNotificationsEnabled"),
    },
    {
      value: isEmployeeRefActive,
      hidden: !isSmsNotificationsActive,
      title: props.t("INCLUDE_EMPLOYEE_REF"),
      description: props.t("EMOLOYEE_REF_SWITCH_DESCRIPTION"),
      onChange: updateManagerSmsSettings("includeEmployeeRef"),
    },
    {
      value: isAbsenceRemindersActive,
      title: props.t("EMPLOYEE_SMS_OVERDUE_REMINDERS_TEXT"),
      description: props.t("OVERDUE_REMINDERS_SWITCH_DESCRIPTION"),
      onChange: updateEmployeeSmsSetting("overdueAbsenceReminders"),
    },
    {
      value: isTextToCloseActive,
      title: props.t("EMPLOYEE_SMS_TEXT_TO_CLOSE_TEXT"),
      description: props.t("TEXT_TO_CLOSE_SWITCH_DESCRIPTION"),
      onChange: updateEmployeeSmsSetting("textToClose"),
    },
  ];

  return (
    <View gap={6} margin="2rem 0 0 0">
      <View gap={3}>
        <Text type="h5" text={props.t("MANAGER_SMS")} />
        {props.settings && (
          <View gap={3}>
            {switchData
              .filter(item => !item.hidden)
              .map(item => (
                <BoxField key={item.title} title={item.title} description={item.description}>
                  <SwitchButton active={item.value} onChange={item.onChange} />
                </BoxField>
              ))}
          </View>
        )}
      </View>
      <View gap={3}>
        <View direction="row" justifyContent="space-between" alignItems="center">
          <Text type="h5" text={props.t("NOTIFICATIONS_SEND_TIMES")} />
          <Button
            materialIcon="add_box"
            disabled={!isSmsNotificationsActive}
            onClick={props.onAdd}
            label={props.t("ADD")}
          />
        </View>
        {props.list && <GSGrid rows={props.list} columns={columns} />}
      </View>
    </View>
  );
}

export default localize()(ManagerSmsNotificationsSmsList);
